// SSL / TLS — Account-wide Overview tab.
//
// Backed by the new GET /config/ssl/overview endpoint (see
// service/config/ssl_overview.js). All data is real: per-site cert
// fields come from forge-parsed PEM, aggregate counters are computed
// server-side, traffic comes from CH (layerd-v2-bm-events tls_version
// distribution, last 24h, filtered to the accessible sites).
//
// No hard-coded values. Cells with no data show "-" + an honest
// explanation rather than fabricating numbers.

import React from "react";
import {
  Box, Stack, Typography,
  Table, TableBody, TableCell, TableHead, TableRow, TablePagination, Skeleton, CircularProgress, Tooltip,
  Select, MenuItem,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockIcon from "@mui/icons-material/Lock";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/UploadFile";
import SearchIcon from "@mui/icons-material/Search";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RepeatIcon from "@mui/icons-material/Repeat";
import CloseIcon from "@mui/icons-material/Close";
import LandmarkIcon from "@mui/icons-material/AccountBalanceOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SparklesIcon from "@mui/icons-material/AutoAwesome";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import colors from "../../../../utils/colors";
import useSSLConfig from "../../../../hooks/user/useSSLConfig";
import useHome from "../../../../hooks/user/useHome";
import { SslType } from "../../../../utils/constants";
import SSLOverviewMetricCard, {
  SSLOverviewIconChip,
  SSL_OVERVIEW_ICON_SX,
  sslOverviewGridSx,
  sslOverviewLabelSx,
  sslOverviewSubSx,
  sslMetricCardSx,
  sslMetricCardHighlightSx,
} from "./components/SSLOverviewMetricCard";
import SSLTlsPostureCard from "./components/SSLTlsPostureCard";
import {
  certsBodySx,
  certsBodySecondarySx,
  certsBodySmallSx,
  certsCardTitleSx,
  certsStatSx,
  certsGradeDisplaySx,
  certsPillTextSx,
  certsRowTitleSx,
  certsFieldValueSx,
} from "./certsTypography";
import { SafeTextField } from "../../../common/Greens";
import {
  CERTS_FAINT_GREY,
  CERTS_PAGE_SECTION_SPACING,
  CERTS_CARD_GRID_GAP,
  CERTS_GRID_GAP,
  certsPanelHeaderSx,
  certsRecItemSx,
  certsCardShellSx,
} from "./certsLayout";
import { filterSelectSx } from "../services/api_security/asecUi";

const CERT_FILTER_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "EXP30", label: "Expiring <30d" },
  { value: "GRADE_A", label: "A+ grade" },
  { value: "AUTO_OFF", label: "Auto-renew off" },
];

const INDIGO       = colors.primary[300];
const INDIGO_INK   = colors.primary[500];
const INDIGO_DEEP  = colors.primary[400];
const INDIGO_SOFT  = "rgba(99,113,247,0.10)";
const INDIGO_MID   = "rgba(99,113,247,0.18)";
const INDIGO_BORDER= "rgba(99,113,247,0.30)";
const GREEN_DARK   = colors.green[300];
const RED_DARK     = colors.red[200];
const FAINT_GREY   = CERTS_FAINT_GREY;

const nfmt = (n) => (typeof n === "number" && isFinite(n) ? n.toLocaleString() : "—");
const pct = (n, d = 1) => (typeof n === "number" && isFinite(n) ? `${n.toFixed(d)}%` : "—");
const dotSx = { width: 6, height: 6, borderRadius: "50%", backgroundColor: "currentColor", display: "inline-block" };

// Shared dark tooltip chrome (matches Aggregate TLS grade help on this page).
const CERTS_HELP_TOOLTIP_SX = {
  maxWidth: 360,
  fontSize: 12,
  lineHeight: "18px",
  color: "#fff",
  backgroundColor: "#1F2937",
  "& .MuiTooltip-arrow": { color: "#1F2937" },
  p: 1.5,
};

const MIX_CARD_HELP = {
  tlsVersion:
    "Share of TLS protocol versions used in handshakes over the last 24 hours, from live traffic telemetry for your accessible sites.",
  encryptionMode:
    "How many sites use each SSL/TLS encryption mode (Advanced E2E, Adaptive E2E, Adaptive, InSecure).",
  issuerMix:
    "Breakdown of certificate issuers across sites that report an issuer on the installed certificate.",
  providerMix:
    "Breakdown of certificate providers across sites with an installed host certificate. Custom includes uploads and legacy certificates.",
};

function MixCardHelpIcon({ title, label }) {
  return (
    <Tooltip
      title={title}
      arrow
      placement="top"
      componentsProps={{ tooltip: { sx: CERTS_HELP_TOOLTIP_SX } }}
    >
      <Box
        component="span"
        tabIndex={0}
        role="img"
        aria-label={label}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          flexShrink: 0,
          lineHeight: 0,
          borderRadius: "4px",
          outline: "none",
          "&:focus-visible": {
            boxShadow: `0 0 0 2px ${INDIGO_BORDER}`,
          },
        }}
      >
        <InfoOutlinedIcon
          sx={{
            fontSize: 14,
            color: colors.primary[300],
            cursor: "help",
          }}
          aria-hidden
        />
      </Box>
    </Tooltip>
  );
}

const SSL_TYPE_LABEL = {
  [SslType.OFF]:         { label: "InSecure",     tone: "default" },
  [SslType.FLEXIBLE]:    { label: "Adaptive",     tone: "warn" },
  [SslType.FULL]:        { label: "Adaptive E2E", tone: "ok" },
  [SslType.FULL_STRICT]: { label: "Advanced E2E", tone: "info" },
};

// Map cert provider id → display label + tone for the table badge.
// Falls back to a neutral "-" when a cert was installed via a path
// that doesn't yet stamp `certs.provider` (custom uploads, legacy
// ZeroSSL via the old generate_certs flow).
const PROVIDER_BADGE = {
  zerossl:  { label: "Universal",  tone: "info" },
  acm:      { label: "Amazon",     tone: "warn" },
  digicert: { label: "DigiCert",   tone: "ok" },
  custom:   { label: "Custom",     tone: "default" },
};
function Pill({ tone = "default", children, sx }) {
  const tones = {
    default: { bg: colors.neutral[100], fg: colors.neutral[700], bd: colors.neutral[200] },
    ok:      { bg: "rgba(31,193,107,0.10)", fg: GREEN_DARK,         bd: "rgba(31,193,107,0.30)" },
    warn:    { bg: "rgba(255,219,67,0.18)", fg: "#7a5b10",          bd: "rgba(223,180,0,0.40)" },
    danger:  { bg: "rgba(251,55,72,0.10)",  fg: RED_DARK,           bd: "rgba(251,55,72,0.30)" },
    info:    { bg: INDIGO_SOFT,             fg: INDIGO_INK,         bd: INDIGO_BORDER },
  };
  const t = tones[tone] || tones.default;
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5,
      px: 1.25, height: 24, borderRadius: 999,
      fontSize: 12, fontWeight: 500, whiteSpace: "nowrap",
      backgroundColor: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
      ...sx,
    }}>{children}</Box>
  );
}

function GradePill({ grade }) {
  if (!grade) {
    return <Typography sx={{ ...certsBodySmallSx, color: colors.neutral[500] }}>—</Typography>;
  }
  const palette = (() => {
    const g = (grade || "").toUpperCase();
    if (g === "A+" || g === "A") return { bg: "rgba(31,193,107,0.10)", fg: GREEN_DARK, bd: "rgba(31,193,107,0.30)" };
    if (g === "B")               return { bg: "rgba(255,219,67,0.18)", fg: "#7a5b10",  bd: "rgba(223,180,0,0.40)" };
    if (g === "C")               return { bg: "rgba(255,219,67,0.18)", fg: "#7a5b10",  bd: "rgba(223,180,0,0.40)" };
    if (g === "D" || g === "F")  return { bg: "rgba(251,55,72,0.10)",  fg: RED_DARK,   bd: "rgba(251,55,72,0.30)" };
    return { bg: colors.neutral[100], fg: colors.neutral[600], bd: colors.neutral[200] };
  })();
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      minWidth: 38, height: 24, px: 1, borderRadius: "6px",
      ...certsPillTextSx, fontWeight: 800, letterSpacing: "0.02em",
      backgroundColor: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}`,
    }}>{grade || "—"}</Box>
  );
}

// ─── Rating ring gauge — identical to Configuration page ────────────
function RatingRing({ grade, score }) {
  const palette = (() => {
    const g = (grade || "").toUpperCase();
    if (g === "A+" || g === "A") return { stroke: GREEN_DARK, text: GREEN_DARK, fillPct: 96 };
    if (g === "B")               return { stroke: "#F2B429",  text: "#7a5b10",  fillPct: 65 };
    if (g === "C")               return { stroke: "#F2B429",  text: "#7a5b10",  fillPct: 50 };
    if (g === "D")               return { stroke: RED_DARK,   text: RED_DARK,   fillPct: 30 };
    if (g === "F")               return { stroke: RED_DARK,   text: RED_DARK,   fillPct: 15 };
    return { stroke: colors.neutral[400], text: colors.neutral[600], fillPct: 0 };
  })();
  const pctV = typeof score === "number" ? Math.max(0, Math.min(100, score)) : palette.fillPct;
  const circ = 2 * Math.PI * 32;
  return (
    <Box sx={{ position: "relative", width: 74, height: 74, flexShrink: 0 }}>
      <svg width={74} height={74} viewBox="0 0 74 74" style={{ transform: "rotate(-90deg)" }}>
        <circle cx={37} cy={37} r={32} fill="none" stroke="#F4F4F4" strokeWidth={6} />
        <circle cx={37} cy={37} r={32} fill="none"
          stroke={palette.stroke} strokeWidth={6} strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pctV / 100)} />
      </svg>
      <Box sx={{
        position: "absolute", inset: 0, display: "flex",
        alignItems: "center", justifyContent: "center",
        ...certsGradeDisplaySx,
        color: palette.text, letterSpacing: "-0.02em",
      }}>{grade || "—"}</Box>
    </Box>
  );
}

function ratingHeadline(g) {
  const u = (g || "").toUpperCase();
  if (u === "A+") return "Excellent · best practice";
  if (u === "A")  return "Excellent";
  if (u === "B")  return "Good · room to improve";
  if (u === "C")  return "Fair · improvements recommended";
  if (u === "D")  return "Poor · action needed";
  if (u === "F")  return "Failing · urgent action";
  return "Not yet scanned";
}

const sslOverviewHeroValueSx = {
  ...certsStatSx,
};

// ─── HERO ───────────────────────────────────────────────────────────
function HeroStrip({ overview, loading }) {
  const agg     = overview?.aggregates;
  const traffic = overview?.traffic_24h;
  const tls13   = traffic?.tls_versions?.find((v) => v.tls_version === "TLSv1.3");
  const tls12   = traffic?.tls_versions?.find((v) => v.tls_version === "TLSv1.2");
  const legacy  = traffic?.tls_versions
    ?.filter((v) => v.tls_version !== "TLSv1.3" && v.tls_version !== "TLSv1.2")
    .reduce((acc, v) => acc + (v.pct || 0), 0);

  const total  = agg?.total_certs ?? 0;
  const grades = agg?.grade_summary || {};
  const aAndAbove = (grades["A+"] || 0) + (grades["A"] || 0);
  const others    = Object.entries(grades).filter(([k]) => k !== "A+" && k !== "A");

  const metricIcon = (IconComponent) => (
    <SSLOverviewIconChip variant="primary">
      <IconComponent sx={SSL_OVERVIEW_ICON_SX} />
    </SSLOverviewIconChip>
  );

  return (
    <Box sx={{ ...sslOverviewGridSx, gap: CERTS_CARD_GRID_GAP }}>
      <Box sx={{ ...sslMetricCardSx, ...sslMetricCardHighlightSx, justifyContent: "flex-start" }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
          {metricIcon(StarOutlineIcon)}
          <Typography component="span" sx={sslOverviewLabelSx}>Aggregate TLS grade</Typography>
          <Tooltip
            title="Certificate scan grade across your sites (worst grade present). Separate from TLS posture — posture checks HSTS, negotiated TLS versions, and rewrite settings, so Excellent grade can still show needs attention there."
            arrow
            placement="top"
            componentsProps={{
              tooltip: { sx: CERTS_HELP_TOOLTIP_SX },
            }}
          >
            <HelpOutlineIcon sx={{
              fontSize: 14,
              color: colors.primary[300],
              cursor: "help",
              flexShrink: 0,
            }} />
          </Tooltip>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.75} sx={{ width: "100%" }}>
          {loading ? (
            <Box sx={{ width: 74, height: 74, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <CircularProgress size={56} sx={{ color: INDIGO }} />
            </Box>
          ) : (
            <RatingRing grade={agg?.aggregate_grade} score={agg?.aggregate_score} />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {loading ? (
              <Skeleton width={150} height={22} />
            ) : agg?.aggregate_grade ? (
              <>
                <Typography sx={{ ...certsBodySx, fontWeight: 700 }}>
                  {ratingHeadline(agg.aggregate_grade)}
                </Typography>
                <Typography sx={{ ...certsBodySecondarySx, mt: 0.25 }}>
                  {typeof agg?.aggregate_score === "number" && (
                    <>{agg.aggregate_score} / 100 · </>
                  )}
                  {aAndAbove > 0 && <><Box component="span" sx={{ fontWeight: 700, color: GREEN_DARK }}>{aAndAbove}</Box> A or A+</>}
                  {aAndAbove > 0 && others.length > 0 && <> · </>}
                  {others.map(([g, n], i) => (
                    <React.Fragment key={g}>
                      <Box component="span" sx={{ fontWeight: 700, color: g === "B" || g === "C" ? colors.secondary[400] : RED_DARK }}>{n}</Box> {g}
                      {i < others.length - 1 ? " · " : ""}
                    </React.Fragment>
                  ))}
                  {(aAndAbove + others.reduce((a, [, n]) => a + n, 0)) === 0 && "no ratings yet"}
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={{ ...certsBodySmallSx, fontWeight: 600 }}>Not yet scanned</Typography>
                <Typography sx={{ ...certsBodySecondarySx, mt: 0.25 }}>no ratings yet</Typography>
              </>
            )}
          </Box>
        </Stack>
      </Box>

      <SSLOverviewMetricCard icon={metricIcon(WorkspacePremiumOutlinedIcon)} label="Certificates">
        {loading ? <Skeleton width={80} height={28} sx={{ mt: 1.25, borderRadius: "6px" }} /> : (
          <>
            <Typography sx={sslOverviewHeroValueSx}>
              {nfmt(total)}{" "}
              <Box component="span" sx={{ ...certsBodySmallSx, fontWeight: 500, color: colors.neutral[700] }}>/ {nfmt(agg?.active_certs)} active</Box>
            </Typography>
            <Typography sx={sslOverviewSubSx}>{nfmt(agg?.total_sites)} sites · {nfmt(agg?.expired_certs)} expired</Typography>
          </>
        )}
      </SSLOverviewMetricCard>

      <SSLOverviewMetricCard icon={metricIcon(CalendarMonthOutlinedIcon)} label="Expiring soon">
        {loading ? <Skeleton width={80} height={28} sx={{ mt: 1.25, borderRadius: "6px" }} /> : (
          <>
            <Typography sx={{
              ...sslOverviewHeroValueSx,
              color: agg?.expiring_14d > 0 ? colors.secondary[400] : colors.neutral[1000],
            }}>
              {nfmt(agg?.expiring_14d)}{" "}
              <Box component="span" sx={{ ...certsBodySmallSx, fontWeight: 500, color: colors.neutral[700] }}>in &lt; 14 d</Box>
            </Typography>
            <Typography sx={sslOverviewSubSx}>
              {agg?.earliest_expiry
                ? <><Box component="span" sx={{ fontWeight: 700, color: colors.secondary[400] }}>{agg.earliest_expiry.site_id}</Box> · {agg.earliest_expiry.days}d left</>
                : "all sites have time"}
            </Typography>
          </>
        )}
      </SSLOverviewMetricCard>

      <SSLOverviewMetricCard icon={metricIcon(QueryStatsOutlinedIcon)} label="HTTPS traffic · 24h">
        {loading ? <Skeleton width={100} height={28} sx={{ mt: 1.25, borderRadius: "6px" }} /> : traffic ? (
          <>
            <Typography sx={sslOverviewHeroValueSx}>{nfmt(traffic.total_requests)}</Typography>
            <Typography sx={sslOverviewSubSx}>across {nfmt(agg?.total_sites)} sites</Typography>
          </>
        ) : (
          <>
            <Typography sx={{ ...sslOverviewHeroValueSx, color: colors.neutral[500] }}>—</Typography>
            <Typography sx={sslOverviewSubSx}>No traffic captured in the last 24 h.</Typography>
          </>
        )}
      </SSLOverviewMetricCard>

      <SSLOverviewMetricCard
        icon={metricIcon(GppGoodOutlinedIcon)}
        label="TLS 1.3 share"
        sx={{ gridColumn: { xs: "auto", sm: "auto", md: "1 / -1", lg: "auto" } }}
      >
        {loading ? <Skeleton width={90} height={28} sx={{ mt: 1.25, borderRadius: "6px" }} /> : tls13 ? (
          <>
            <Typography sx={{ ...sslOverviewHeroValueSx, color: GREEN_DARK }}>{pct(tls13.pct)}</Typography>
            <Typography sx={sslOverviewSubSx}>{pct(tls12?.pct ?? 0)} on 1.2 · {pct(legacy ?? 0)} legacy</Typography>
          </>
        ) : (
          <>
            <Typography sx={{ ...sslOverviewHeroValueSx, color: colors.neutral[500] }}>—</Typography>
            <Typography sx={sslOverviewSubSx}>No handshake telemetry yet.</Typography>
          </>
        )}
      </SSLOverviewMetricCard>
    </Box>
  );
}

// ─── 180-day expiry timeline ───────────────────────────────────────
/** Layerd-UI .tl-dot .nm — prefer cert SAN/host, else site id */
function timelineSiteName(site) {
  const hosts = Array.isArray(site.certs?.host)
    ? site.certs.host
    : site.certs?.host ? [site.certs.host] : [];
  if (hosts[0]) return String(hosts[0]);
  return site.site_id || "—";
}

/** Rough half-width of a marker label in % of the timeline track (11.5px Rubik). */
function timelineLabelHalfWidthPct(label) {
  const chars = Math.min((label || "").length, 18);
  return Math.min(10, 2 + chars * 0.45);
}

const TL_LABEL_GAP_PCT = 1.5;
/** Prefer opposite sides of the axis first so nearby days (87/88/89) do not stack. */
const TL_LANES = [
  { top: "22%", above: true },
  { top: "56%", above: false },
  { top: "0%", above: true },
  { top: "80%", above: false },
];
const TL_PLOT_H = 280;
/** Room for a centered 140px label at TODAY / last tick without clipping. */
const TL_PLOT_PAD_X = 80;

function timelineMarkerOverlaps(layouts, left, halfWidth, lane) {
  return layouts.some(
    (p) => p.lane === lane
      && Math.abs(left - p.left) < p.halfWidth + halfWidth + TL_LABEL_GAP_PCT
  );
}

function timelineDisplayName(group) {
  const first = timelineSiteName(group[0]);
  if (group.length === 1) return first;
  return `${first} +${group.length - 1}`;
}

function timelineTooltipTitle(group) {
  return group.map((s) => timelineSiteName(s)).join("\n");
}

/**
 * One marker per expiry day. Same-day sites share a label; nearby days use
 * extra vertical lanes so names do not stack on top of each other.
 */
function layoutExpiryPoints(sites, windowDays) {
  const byDay = new Map();
  (sites || []).forEach((s) => {
    if (typeof s.days_until_expiry !== "number" || s.days_until_expiry < 0 || s.days_until_expiry > windowDays) return;
    const days = s.days_until_expiry;
    const existing = byDay.get(days);
    if (existing) existing.group.push(s);
    else {
      byDay.set(days, {
        days,
        group: [s],
        left: (days / windowDays) * 100,
      });
    }
  });

  const items = Array.from(byDay.values())
    .map((item) => {
      item.group.sort((a, b) => timelineSiteName(a).localeCompare(timelineSiteName(b)));
      const name = timelineDisplayName(item.group);
      return { ...item, name, halfWidth: timelineLabelHalfWidthPct(name) };
    })
    .sort((a, b) => a.days - b.days || a.name.localeCompare(b.name));

  const layouts = [];
  items.forEach((item) => {
    let lane = 0;
    for (let candidate = 0; candidate < TL_LANES.length; candidate += 1) {
      if (!timelineMarkerOverlaps(layouts, item.left, item.halfWidth, candidate)) {
        lane = candidate;
        break;
      }
      lane = candidate;
    }

    layouts.push({
      days: item.days,
      group: item.group,
      left: item.left,
      lane,
      above: TL_LANES[lane].above,
      halfWidth: item.halfWidth,
      labelName: item.name,
    });
  });
  return layouts;
}

const tlDotNameSx = {
  fontSize: 11.5,
  fontWeight: 600,
  lineHeight: "14px",
  mt: 0.625,
  whiteSpace: "nowrap",
  color: colors.neutral[1000],
};

const tlDotDaysSx = (tone) => ({
  fontSize: 10.5,
  lineHeight: "13px",
  fontVariantNumeric: "tabular-nums",
  mt: 0.125,
  color: tone === "danger" ? RED_DARK : tone === "warn" ? colors.secondary[400] : colors.neutral[700],
  fontWeight: tone === "danger" || tone === "warn" ? 600 : 400,
});

function ExpiryTimeline({ sites, loading }) {
  const [windowDays, setWindowDays] = React.useState(180);
  const eligible = (sites || []).filter((s) => typeof s.days_until_expiry === "number" && s.days_until_expiry >= 0 && s.days_until_expiry <= windowDays);
  const points = React.useMemo(() => layoutExpiryPoints(eligible, windowDays), [eligible, windowDays]);
  const ticks = (() => {
    if (windowDays === 90)  return [0, 15, 30, 45, 60, 75, 90];
    if (windowDays === 180) return [0, 30, 60, 90, 120, 150, 180];
    return [0, 60, 120, 180, 240, 300, 365];
  })();
  return (
    <Box sx={{
      ...certsCardShellSx,
      p: "20px",
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CalendarMonthOutlinedIcon sx={{ fontSize: 17, color: INDIGO }} />
          <Typography sx={certsCardTitleSx}>Expiry timeline</Typography>
        </Stack>
        <Stack direction="row" sx={{
          backgroundColor: FAINT_GREY, border: `1px solid ${colors.neutral[200]}`,
          borderRadius: "8px", p: "3px",
        }}>
          {[90, 180, 365].map((d) => (
            <Box key={d} onClick={() => setWindowDays(d)} sx={{
              px: 1.4, py: 0.5, fontSize: 12, fontWeight: 500, borderRadius: "6px", cursor: "pointer",
              backgroundColor: windowDays === d ? colors.neutral[100] : "transparent",
              color: windowDays === d ? colors.neutral[1000] : colors.neutral[700],
              boxShadow: windowDays === d ? "0 1px 2px rgba(17,17,17,0.06)" : "none",
            }}>{d} d</Box>
          ))}
        </Stack>
      </Stack>
      <Typography sx={{
        ...certsBodySecondarySx,
        fontSize: 11,
        fontWeight: 400,
        mt: 0.5,
        letterSpacing: "normal",
        textTransform: "none",
      }}>Each marker shows the site and days remaining</Typography>

      {loading ? (
        <Box sx={{ height: TL_PLOT_H, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Box sx={{ position: "relative", height: TL_PLOT_H, mt: 4, mx: `${TL_PLOT_PAD_X}px` }}>
          {/* Danger band 0..14d */}
          <Box sx={{
            position: "absolute", top: "calc(50% - 6px)", height: 12, borderRadius: 1,
            left: 0, width: `${(14 / windowDays) * 100}%`,
            backgroundColor: "rgba(251,55,72,0.10)",
            borderLeft: "2px dashed rgba(251,55,72,0.40)",
          }} />
          {/* Warn band 14..30d */}
          <Box sx={{
            position: "absolute", top: "calc(50% - 6px)", height: 12, borderRadius: 1,
            left: `${(14 / windowDays) * 100}%`, width: `${((30 - 14) / windowDays) * 100}%`,
            backgroundColor: "rgba(255,219,67,0.18)",
            borderLeft: "2px dashed rgba(223,180,0,0.50)",
          }} />
          {/* Axis */}
          <Box sx={{ position: "absolute", left: 0, right: 0, top: "50%", height: "1px", backgroundColor: colors.neutral[200] }} />
          {/* Ticks */}
          {ticks.map((d, i) => (
            <Box key={d} sx={{
              position: "absolute", top: "50%", left: `${(d / windowDays) * 100}%`,
              transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center",
            }}>
              <Box sx={{ width: i === 0 ? "2px" : "1px",
                height: i === 0 ? "14px" : "7px",
                backgroundColor: i === 0 ? INDIGO : colors.neutral[200],
                marginTop: i === 0 ? "-7px" : 0 }} />
              <Typography sx={{
fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
                color: i === 0 ? INDIGO : colors.neutral[600], mt: 0.5, whiteSpace: "nowrap",
              }}>{i === 0 ? "TODAY" : `+${d}d`}</Typography>
            </Box>
          ))}
          {/* Dots */}
          {eligible.length === 0 && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: colors.neutral[600], ...certsBodySmallSx }}>
              No certificates expiring within the next {windowDays} day{windowDays === 1 ? "" : "s"}.
            </Box>
          )}
          {points.map(({ days, group, left, lane, above, labelName }) => {
            const site = group[0];
            const tone = days < 14 ? "danger" : days < 30 ? "warn" : "ok";
            const ringColor = tone === "danger" ? "#D00416" : tone === "warn" ? "#DFB400" : "#178F4F";
            const tooltipTitle = timelineTooltipTitle(group);
            const showTooltip = group.length > 1 || (labelName || "").length > 18;
            const laneTop = TL_LANES[lane]?.top || (above ? "18%" : "64%");

            const marker = (
              <Box
                sx={{
                  position: "absolute",
                  left: `${left}%`,
                  top: laneTop,
                  transform: "translateX(-50%)",
                  textAlign: "center",
                  maxWidth: 140,
                  cursor: showTooltip ? "help" : "default",
                  transition: "transform 160ms",
                  zIndex: 1,
                  "&:hover": { transform: "translateX(-50%) translateY(-2px)", zIndex: 3 },
                }}
              >
                <Box sx={{ display: "inline-flex", mx: "auto" }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: ringColor,
                    boxShadow: `0 0 0 2px ${colors.neutral[100]}, 0 0 0 3.5px ${ringColor}`,
                  }} />
                </Box>
                <Typography
                  sx={{
                    ...tlDotNameSx,
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "center",
                    maxWidth: 140,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="span"
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {timelineSiteName(site)}
                  </Box>
                  {group.length > 1 && (
                    <Box
                      component="span"
                      sx={{
                        flexShrink: 0,
                        ml: 0.4,
                        color: INDIGO,
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      +{group.length - 1}
                    </Box>
                  )}
                </Typography>
                <Typography sx={tlDotDaysSx(tone)}>
                  {days} day{days === 1 ? "" : "s"}
                </Typography>
              </Box>
            );

            return showTooltip ? (
              <Tooltip
                key={`exp-${site.site_uid}-${days}`}
                title={<span style={{ whiteSpace: "pre-line" }}>{tooltipTitle}</span>}
                arrow
                placement={above ? "top" : "bottom"}
              >
                {marker}
              </Tooltip>
            ) : (
              <React.Fragment key={`exp-${site.site_uid}-${days}`}>
                {marker}
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ─── Certificates table (Layerd-UI ssl-tls/index.html .tbl) ───────
function domainSubline(site) {
  const meta = PROVIDER_BADGE[site.certs?.provider];
  const providerLabel = meta?.label || "Custom";
  const hosts = Array.isArray(site.certs?.host)
    ? site.certs.host
    : site.certs?.host ? [site.certs.host] : [];
  if (hosts.length > 1) return `${providerLabel} · ${hosts.length} SANs`;
  if (hosts.some((h) => String(h).includes("*"))) return `${providerLabel} · wildcard`;
  return `${providerLabel} · ${hosts[0] || "leaf cert"}`;
}

function sanDisplay(site) {
  const hosts = Array.isArray(site.certs?.host)
    ? site.certs.host
    : site.certs?.host ? [site.certs.host] : [];
  if (!hosts.length) return site.site_id;
  return hosts.join(", ");
}

function expiresDisplay(site) {
  if (!site.certs?.validTo) return null;
  const d = new Date(site.certs.validTo);
  const monthDay = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const days = site.days_until_expiry;
  const expiringSoon = days != null && days < 30 && days >= 0;
  return {
    line: `${monthDay}${days != null ? ` · ${days}d` : ""}`,
    sub: expiringSoon && site.auto_renew ? "renewal queued" : null,
    expiringSoon,
  };
}

const CERTS_PAGE_SIZE_OPTIONS = [10, 25, 50];
const CERTS_DEFAULT_PAGE_SIZE = 10;
const certsTablePaginationSx = {
  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
    fontSize: 14, color: colors.neutral[700],
  },
};

function CertificatesTable({
  sites,
  sitesTotal,
  loading,
  onRowClick,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  search,
  onSearchChange,
  filter,
  onFilterChange,
  filterCounts,
}) {
  const cols = ["Domain", "Issuer", "SAN", "Encryption mode", "Expires", "Grade", "Chain", "Auto-renew", ""];
  const total = typeof sitesTotal === "number" ? sitesTotal : (sites || []).length;
  const paged = sites || [];
  const counts = filterCounts || {
    ALL: total,
    EXP30: 0, AUTO_OFF: 0, GRADE_A: 0,
  };

  return (
    <Box
      className="ssl-certs-table"
      sx={{
      ...certsCardShellSx,
      fontSize: 14,
      fontVariantNumeric: "tabular-nums",
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={certsPanelHeaderSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <VerifiedUserIcon sx={{ fontSize: 16, color: INDIGO }} />
          <Typography sx={certsCardTitleSx}>
            Certificates
          </Typography>
          <Pill sx={{ ml: 0.5 }}>{nfmt(total)} site{total === 1 ? "" : "s"}</Pill>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        sx={{
          px: "18px",
          py: "12px",
          gap: 1,
          borderBottom: `1px solid ${colors.neutral[200]}`,
          backgroundColor: FAINT_GREY,
          flexWrap: "wrap",
          rowGap: 1.25,
        }}
      >
        <Box sx={{
          width: { xs: "100%", md: 360 },
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: 34,
          px: 1.5,
          borderRadius: "8px",
          backgroundColor: colors.neutral[100],
          border: `1px solid ${colors.neutral[200]}`,
        }}>
          <SearchIcon sx={{ fontSize: 14, color: colors.neutral[500], flexShrink: 0 }} />
          <SafeTextField
            variant="standard"
            fullWidth
            placeholder="Search domain, issuer or SAN…"
            value={search}
            onChange={(e) => onSearchChange?.(e.target.value)}
            InputProps={{
              disableUnderline: true,
              sx: { ...certsBodySmallSx, lineHeight: "21px" },
            }}
            sx={{ flex: 1, minWidth: 0 }}
          />
        </Box>

        <Select
          value={filter}
          onChange={(e) => onFilterChange?.(e.target.value)}
          sx={{ ...filterSelectSx, minWidth: 200, flexShrink: 0 }}
          renderValue={(value) => {
            const opt = CERT_FILTER_OPTIONS.find((o) => o.value === value) || CERT_FILTER_OPTIONS[0];
            const count = counts[opt.value] ?? 0;
            return `${opt.label} (${nfmt(count)})`;
          }}
        >
          {CERT_FILTER_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label} ({nfmt(counts[opt.value] ?? 0)})
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Table size="small" sx={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            {cols.map((h, i) => (
              <TableCell key={h} sx={{ ...headCellSx, ...(i === 0 ? { width: "24%" } : {}), ...(h === "" ? { width: 40 } : {}) }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? Array.from({ length: 3 }).map((_, i) => (
            <TableRow key={i}>{cols.map((c) => <TableCell key={c} sx={cellSx}>
              <Skeleton variant="rectangular" height={20} sx={{ borderRadius: "6px" }} /></TableCell>)}</TableRow>
          )) : total === 0 ? (
            <TableRow>
              <TableCell colSpan={cols.length} align="center" sx={{ ...cellSx, py: 6, color: colors.neutral[600], ...certsBodySmallSx }}>
                {counts.ALL === 0 ? "No sites in this project yet." : "No sites match the current filter."}
              </TableCell>
            </TableRow>
          ) : paged.map((s) => {
            const mode = SSL_TYPE_LABEL[s.ssl_type];
            const exp = expiresDisplay(s);
            const expiringSoon = exp?.expiringSoon;
            return (
              <TableRow key={s.site_uid} hover onClick={() => onRowClick(s)}
                sx={{
                  cursor: "pointer",
                  backgroundColor: expiringSoon ? "rgba(242, 180, 41, 0.05)" : "transparent",
                  "&:hover": { backgroundColor: expiringSoon ? "rgba(242, 180, 41, 0.10)" : FAINT_GREY },
                  "&:last-child td": { borderBottom: 0 },
                }}>
                <TableCell sx={cellSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LockIcon sx={{ fontSize: 18, width: 18, height: 18, color: INDIGO, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={ { ...certsFieldValueSx, fontSize: 14, lineHeight: "21px" }}>
                        {s.site_id}
                      </Typography>
                      <Typography sx={{
                        ...certsBodySmallSx,
                        lineHeight: "18px",
                        color: colors.neutral[700],
                        mt: "2px",
                      }}>
                        {domainSubline(s)}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={cellSx}>
                  <Typography sx={ { ...certsBodySmallSx, lineHeight: "20px" }}>
                    {s.certs?.issuer || "—"}
                  </Typography>
                </TableCell>
                <TableCell sx={cellSx}>
                  <Typography sx={{
                    ...certsBodySmallSx,
                    lineHeight: "20px",
                    color: colors.neutral[600],
                  }}>
                    {sanDisplay(s)}
                  </Typography>
                </TableCell>
                <TableCell sx={cellSx}>
                  {mode
                    ? <Pill tone={mode.tone}><Box sx={dotSx} />{mode.label}</Pill>
                    : <Typography sx={{ ...certsBodySmallSx, color: colors.neutral[500] }}>—</Typography>}
                </TableCell>
                <TableCell sx={cellSx}>
                  {exp ? (
                    <Box>
                      <Typography sx={{
                        ...certsFieldValueSx,
                        fontSize: 14,
                        lineHeight: "20px",
                        color: exp.expiringSoon ? colors.secondary[400] : colors.neutral[1000],
                      }}>
                        {exp.line}
                      </Typography>
                      {exp.sub ? (
                        <Typography sx={{
                          fontSize: 12,
                          lineHeight: "25px",
                          color: colors.secondary[400],
                          mt: "1px",
                        }}>
                          {exp.sub}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : (
                    <Typography sx={ { ...certsBodySmallSx, color: colors.neutral[500] }}>—</Typography>
                  )}
                </TableCell>
                <TableCell sx={cellSx}><GradePill grade={s.rating?.grade} /></TableCell>
                <TableCell sx={cellSx}>
                  {s.certs?.chain?.length > 0
                    ? <Pill tone="ok"><Box sx={dotSx} />valid</Pill>
                    : <Typography sx={{ ...certsBodySmallSx, color: colors.neutral[500] }}>—</Typography>}
                </TableCell>
                <TableCell sx={cellSx}>
                  {s.auto_renew ? (
                    <Stack direction="row" alignItems="center" spacing={0.625} sx={{ color: GREEN_DARK }}>
                      <RepeatIcon sx={{ fontSize: 15, width: 15, height: 15, color: "inherit" }} />
                      <Typography sx={ { ...certsFieldValueSx, lineHeight: 1 }}>on</Typography>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={0.625} sx={{ color: colors.neutral[500] }}>
                      <CloseIcon sx={{ fontSize: 15, width: 15, height: 15, color: "inherit" }} />
                      <Typography sx={ { ...certsBodySx, fontWeight: 500, lineHeight: 1 }}>off</Typography>
                    </Stack>
                  )}
                </TableCell>
                <TableCell sx={{ ...cellSx, width: 40, px: "13px 12px" }} align="right">
                  <ChevronRightIcon sx={{ fontSize: 18, width: 18, height: 18, color: colors.neutral[500] }} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {!loading && total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => onPageChange?.(next)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            onPageSizeChange?.(parseInt(e.target.value, 10));
          }}
          rowsPerPageOptions={CERTS_PAGE_SIZE_OPTIONS}
          sx={certsTablePaginationSx}
        />
      )}
    </Box>
  );
}

const cellSx = {
  ...certsFieldValueSx,
  fontSize: 14,
  lineHeight: "22px",
  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  py: "14px",
  px: "16px",
  verticalAlign: "middle",
};

const headCellSx = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: colors.neutral[700],
  backgroundColor: FAINT_GREY,
  py: "11px",
  px: "16px",
  borderBottom: `1px solid ${colors.neutral[200]}`,
  lineHeight: 1.2,
};

// ─── Horizontal bar list (TLS version / mode / issuer) ─────────────
function HBarList({ items, loading, fillTone = "ok" }) {
  if (loading) return <Box sx={{ p: 1, textAlign: "center" }}><CircularProgress size={16} /></Box>;
  if (!items || items.length === 0) {
    return <Typography sx={{ p: 1, color: colors.neutral[600], ...certsBodySmallSx }}>No data yet.</Typography>;
  }
  const max = Math.max(...items.map((i) => i.value));
  const palette = {
    ok:     GREEN_DARK,
    warn:   "#F2B429",
    indigo: INDIGO,
    neutral: colors.neutral[400],
  };
  const fillColor = palette[fillTone] || palette.indigo;
  return (
    <Stack spacing={1.25}>
      {items.map((it, i) => (
        <Box key={i}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.6 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              {it.icon}
              <Typography sx={{
                fontSize: 12, fontWeight: 500,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{it.label}</Typography>
              {it.badge && <Pill tone="warn" sx={{ height: 18, fontSize: 10 }}>{it.badge}</Pill>}
            </Stack>
            <Typography sx={{ fontSize: 12, color: colors.neutral[700], fontWeight: 600, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
              {it.right}
            </Typography>
          </Stack>
          <Box sx={{ height: 8, borderRadius: 4, backgroundColor: colors.neutral[100], overflow: "hidden" }}>
            <Box sx={{
              height: "100%", borderRadius: 4,
              width: `${max > 0 ? (it.value / max) * 100 : 0}%`,
              backgroundColor: it.color || fillColor,
              transition: "width 200ms",
            }} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

// ─── Insights ─────────────────────────────────────────────────────
function buildInsights(sites, agg) {
  const recs = [];
  if (!sites) return recs;
  for (const s of sites) {
    if (s.days_until_expiry != null && s.days_until_expiry >= 0 && s.days_until_expiry < 14) {
      recs.push({
        tone: "warn",
        icon: <WarningAmberIcon sx={{ fontSize: 15 }} />,
        title: <>Renew <Box component="span" sx={ { fontWeight: 600 }}>{s.site_id}</Box> · expires in {s.days_until_expiry} day{s.days_until_expiry === 1 ? "" : "s"}</>,
        desc: s.auto_renew
          ? "Auto-renew is on but the cert is still <14 days from expiry — confirm the renewal worker ran."
          : "Auto-renew is OFF. Renew manually or enable Universal SSL to auto-renew.",
        site: s,
      });
    }
  }
  for (const s of sites) {
    if (!s.auto_renew && s.certs?.host) {
      recs.push({
        tone: "info",
        icon: <RepeatIcon sx={{ fontSize: 15 }} />,
        title: <>Auto-renew is off for <Box component="span" sx={ { fontWeight: 600 }}>{s.site_id}</Box></>,
        desc: "Custom cert — set a reminder before expiry or switch to a Layerd-managed cert for automatic renewal.",
        site: s,
      });
    }
  }
  for (const s of sites) {
    if (s.ssl_type !== null && s.ssl_type < SslType.FULL_STRICT && s.certs?.host) {
      recs.push({
        tone: "info",
        icon: <TrendingUpIcon sx={{ fontSize: 15 }} />,
        title: <><Box component="span" sx={ { fontWeight: 600 }}>{s.site_id}</Box> eligible for Advanced E2E</>,
        desc: "Validating the origin cert prevents MITM attacks between Layerd and your backend.",
        site: s,
      });
      break;  // one example is enough
    }
  }
  if (agg && agg.hsts_enabled_count === agg.total_sites && agg.total_sites > 0) {
    recs.push({
      tone: "ok",
      icon: <CheckIcon sx={{ fontSize: 15 }} />,
      title: <>HSTS enabled on all {agg.total_sites} site{agg.total_sites === 1 ? "" : "s"}</>,
      desc: "Browsers will refuse any insecure connection. Great hygiene.",
      site: null,
    });
  }
  return recs;
}

function InsightsCard({ sites, aggregates, loading, onRowClick }) {
  const recs = buildInsights(sites, aggregates);
  return (
    <Box sx={certsCardShellSx}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={certsPanelHeaderSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SparklesIcon sx={{ fontSize: 16, color: INDIGO }} />
          <Typography sx={certsCardTitleSx}>Insights &amp; recommended actions</Typography>
        </Stack>
        {!loading && recs.length > 0 && <Pill tone={recs.some((r) => r.tone === "warn") ? "warn" : "info"}>
          {recs.filter((r) => r.tone !== "ok").length} action{recs.filter((r) => r.tone !== "ok").length === 1 ? "" : "s"}
        </Pill>}
      </Stack>
      <Box>
        {loading ? <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={16} /></Box>
        : recs.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: "center", color: colors.neutral[600], fontSize: 13 }}>
            Everything looks good — no actions needed.
          </Typography>
        ) : recs.map((r, i) => {
          const tones = {
            ok:   { bg: "rgba(31,193,107,0.10)", fg: GREEN_DARK },
            warn: { bg: "rgba(255,219,67,0.18)", fg: "#7a5b10" },
            info: { bg: INDIGO_SOFT,             fg: INDIGO_INK },
          };
          const t = tones[r.tone] || tones.info;
          return (
            <Stack
              key={i}
              direction="row"
              alignItems="flex-start"
              onClick={() => r.site && onRowClick(r.site)}
              sx={{
                ...certsRecItemSx,
                borderBottom: i === recs.length - 1 ? 0 : certsRecItemSx.borderBottom,
                cursor: r.site ? "pointer" : "default",
                "&:hover": r.site ? { backgroundColor: FAINT_GREY } : {},
              }}
            >
              <Box sx={{
                width: 30, height: 30, borderRadius: "8px",
                backgroundColor: t.bg, color: t.fg,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>{r.icon}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ ...certsRowTitleSx, mb: 0.25 }}>{r.title}</Typography>
                <Typography sx={{ ...certsBodySecondarySx, lineHeight: "17px" }}>{r.desc}</Typography>
              </Box>
              {r.site && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: INDIGO, fontSize: 12, fontWeight: 600 }}>
                  Configure<ChevronRightIcon sx={{ fontSize: 14 }} />
                </Stack>
              )}
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Main page ─────────────────────────────────────────────────────
export default function SSLOverviewPage({ onSelectSite, onInstall, onRefresh, refreshTrigger }) {
  const { getSSLOverview } = useSSLConfig();
  const { curProjectId } = useHome();
  const [data, setData] = React.useState(null);
  const [chartSites, setChartSites] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [tablePage, setTablePage] = React.useState(0);
  const [tablePageSize, setTablePageSize] = React.useState(CERTS_DEFAULT_PAGE_SIZE);
  const [tableSearch, setTableSearch] = React.useState("");
  const [tableFilter, setTableFilter] = React.useState("ALL");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(tableSearch), 300);
    return () => clearTimeout(t);
  }, [tableSearch]);

  React.useEffect(() => { setTablePage(0); }, [debouncedSearch, tableFilter, curProjectId]);

  const reloadCharts = React.useCallback(async () => {
    const res = await getSSLOverview(curProjectId);
    if (res?.status === "success") {
      setChartSites(res.data?.sites || []);
      // Keep aggregates/traffic from full payload when available
      setData((prev) => ({
        ...(prev || {}),
        aggregates: res.data?.aggregates,
        traffic_24h: res.data?.traffic_24h,
        sites: prev?.sites || [],
        sites_total: prev?.sites_total,
      }));
    }
  }, [getSSLOverview, curProjectId]);

  const reload = React.useCallback(async () => {
    setLoading(true);
    const res = await getSSLOverview(curProjectId, {
      page: tablePage,
      limit: tablePageSize,
      q: debouncedSearch,
      filter: tableFilter,
    });
    if (res?.status === "success") {
      setData((prev) => ({
        ...(prev || {}),
        ...res.data,
        aggregates: res.data?.aggregates || prev?.aggregates,
        traffic_24h: res.data?.traffic_24h ?? prev?.traffic_24h,
      }));
    } else setData(null);
    setLoading(false);
  }, [getSSLOverview, curProjectId, tablePage, tablePageSize, debouncedSearch, tableFilter]);

  React.useEffect(() => { reloadCharts(); }, [reloadCharts, refreshTrigger]);
  React.useEffect(() => { reload(); }, [reload, refreshTrigger]);

  const sites = data?.sites || [];
  const sitesTotal = typeof data?.sites_total === "number" ? data.sites_total : sites.length;
  const filterCounts = React.useMemo(() => {
    const agg = data?.aggregates || {};
    return {
      ALL: agg.total_sites || sitesTotal,
      EXP30: agg.expiring_30d || 0,
      AUTO_OFF: agg.auto_renew_off_count || 0,
      GRADE_A: (agg.grade_summary?.A || 0) + (agg.grade_summary?.["A+"] || 0),
    };
  }, [data, sitesTotal]);

  // Encryption mode bars from real aggregates.
  const modeBars = data?.aggregates ? [
    { ssl_type: SslType.FULL_STRICT, label: "Advanced E2E", icon: <ShieldOutlinedIcon sx={{ fontSize: 13, color: INDIGO }} />, color: INDIGO },
    { ssl_type: SslType.FULL,        label: "Adaptive E2E", icon: <LockIcon sx={{ fontSize: 13, color: GREEN_DARK }} />, color: GREEN_DARK },
    { ssl_type: SslType.FLEXIBLE,    label: "Adaptive",     icon: <RefreshIcon sx={{ fontSize: 13, color: "#7a5b10" }} />, color: "#F2B429" },
    { ssl_type: SslType.OFF,         label: "InSecure",     icon: <CloseIcon sx={{ fontSize: 13, color: colors.neutral[600] }} />, color: colors.neutral[400] },
  ].map((m) => {
    const n = data.aggregates.ssl_type_mix?.[m.ssl_type] || 0;
    return { label: m.label, icon: m.icon, value: n, right: `${n} site${n === 1 ? "" : "s"}`, color: m.color };
  }) : [];

  // Issuer mix from real aggregates.
  const issuerBars = data?.aggregates?.issuer_mix
    ? Object.entries(data.aggregates.issuer_mix)
        .sort((a, b) => b[1] - a[1])
        .map(([name, n]) => ({ label: name, value: n, right: String(n) }))
    : [];

  // Provider mix — count distinct certs.provider across sites that have
  // an installed cert. Computed client-side so we don't need a new
  // backend field; same source of truth as the table badge.
  const providerBars = React.useMemo(() => {
    const counts = {};
    for (const s of chartSites) {
      if (!s.certs?.host) continue;  // skip sites with no cert yet
      const p = s.certs?.provider || "custom";  // legacy uploads → custom
      counts[p] = (counts[p] || 0) + 1;
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([p, n]) => ({
        label: PROVIDER_BADGE[p]?.label || p,
        value: n,
        right: `${n} site${n === 1 ? "" : "s"}`,
      }));
  }, [chartSites]);

  // TLS version distribution from CH (real handshake telemetry).
  const tlsBars = data?.traffic_24h?.tls_versions
    ? data.traffic_24h.tls_versions.map((v) => ({
        label: v.tls_version || "(unknown)",
        value: v.pct, right: pct(v.pct),
        badge: !["TLSv1.3", "TLSv1.2"].includes(v.tls_version) ? "legacy" : null,
      }))
    : [];

  return (
    <Stack spacing={CERTS_PAGE_SECTION_SPACING}>
      <HeroStrip overview={data} loading={loading} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: CERTS_GRID_GAP, alignItems: "start" }}>
        <ExpiryTimeline sites={chartSites} loading={loading} />
        <SSLTlsPostureCard overview={data} loading={loading} />
      </Box>

      <CertificatesTable
        sites={sites}
        sitesTotal={sitesTotal}
        loading={loading}
        onRowClick={onSelectSite}
        page={tablePage}
        pageSize={tablePageSize}
        onPageChange={setTablePage}
        onPageSizeChange={(n) => { setTablePageSize(n); setTablePage(0); }}
        search={tableSearch}
        onSearchChange={setTableSearch}
        filter={tableFilter}
        onFilterChange={(v) => { setTableFilter(v); setTablePage(0); }}
        filterCounts={filterCounts}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: CERTS_GRID_GAP }}>
        <Box sx={certsCardShellSx}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={certsPanelHeaderSx}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, pr: 1 }}>
              <ShieldOutlinedIcon sx={{ fontSize: 16, color: INDIGO, flexShrink: 0 }} />
              <Typography noWrap sx={{ ...certsCardTitleSx, minWidth: 0 }}>TLS version share · 24h</Typography>
              <MixCardHelpIcon
                title={MIX_CARD_HELP.tlsVersion}
                label="About TLS version share"
              />
            </Stack>
            <Typography sx={{ ...certsBodySecondarySx, flexShrink: 0 }}>
              {data?.traffic_24h?.total_requests != null ? `${nfmt(data.traffic_24h.total_requests)} handshakes` : "—"}
            </Typography>
          </Stack>
          <Box sx={{ p: "18px" }}>
            <HBarList items={tlsBars} loading={loading} fillTone="ok" />
          </Box>
        </Box>

        <Box sx={certsCardShellSx}>
          <Stack direction="row" alignItems="center" sx={certsPanelHeaderSx}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, pr: 1 }}>
              <ShieldOutlinedIcon sx={{ fontSize: 16, color: INDIGO, flexShrink: 0 }} />
              <Typography noWrap sx={{ ...certsCardTitleSx, minWidth: 0 }}>Encryption mode</Typography>
              <MixCardHelpIcon
                title={MIX_CARD_HELP.encryptionMode}
                label="About encryption mode"
              />
            </Stack>
            <Typography sx={{ ...certsBodySecondarySx, flexShrink: 0 }}>
              across {data?.aggregates?.total_sites ?? "…"} sites
            </Typography>
          </Stack>
          <Box sx={{ p: "18px" }}>
            <HBarList items={modeBars} loading={loading} fillTone="indigo" />
          </Box>
        </Box>

        <Box sx={certsCardShellSx}>
          <Stack direction="row" alignItems="center" sx={certsPanelHeaderSx}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, pr: 1 }}>
              <LandmarkIcon sx={{ fontSize: 16, color: INDIGO, flexShrink: 0 }} />
              <Typography noWrap sx={{ ...certsCardTitleSx, minWidth: 0 }}>Issuer mix</Typography>
              <MixCardHelpIcon
                title={MIX_CARD_HELP.issuerMix}
                label="About issuer mix"
              />
            </Stack>
            <Typography sx={{ ...certsBodySecondarySx, flexShrink: 0 }}>
              {issuerBars.length} unique
            </Typography>
          </Stack>
          <Box sx={{ p: "18px" }}>
            <HBarList items={issuerBars} loading={loading} fillTone="indigo" />
          </Box>
        </Box>

        {/* Provider mix — counts certs.provider client-side across the
            same site list we're displaying in the table. Custom and
            legacy uploads fold under "Custom". */}
        <Box sx={certsCardShellSx}>
          <Stack direction="row" alignItems="center" sx={certsPanelHeaderSx}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, pr: 1 }}>
              <ShieldOutlinedIcon sx={{ fontSize: 16, color: INDIGO, flexShrink: 0 }} />
              <Typography noWrap sx={{ ...certsCardTitleSx, minWidth: 0 }}>Provider mix</Typography>
              <MixCardHelpIcon
                title={MIX_CARD_HELP.providerMix}
                label="About provider mix"
              />
            </Stack>
            <Typography sx={{ ...certsBodySecondarySx, flexShrink: 0 }}>
              {providerBars.length} provider{providerBars.length === 1 ? "" : "s"}
            </Typography>
          </Stack>
          <Box sx={{ p: "18px" }}>
            <HBarList items={providerBars} loading={loading} fillTone="indigo" />
          </Box>
        </Box>
      </Box>

      <InsightsCard sites={sites} aggregates={data?.aggregates} loading={loading} onRowClick={onSelectSite} />
    </Stack>
  );
}
